import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';
import type {
  CleanupPreview,
  CleanupRequest,
  CleanupRun,
  SeriesRange,
  StorageBreakdown,
  SystemAlert,
  SystemOverview,
  SystemSeries,
} from './types.ts';

export type AlertStatusFilter = 'open' | 'resolved' | 'all';

/** Every key starts with `system` so one invalidation refreshes the whole page. */
export const systemKeys = {
  all: ['system'] as const,
  overview: ['system', 'overview'] as const,
  series: (range: SeriesRange) => ['system', 'series', range] as const,
  storage: ['system', 'storage'] as const,
  alertsAll: ['system', 'alerts'] as const,
  alerts: (status: AlertStatusFilter) => ['system', 'alerts', status] as const,
  runs: ['system', 'cleanup', 'runs'] as const,
  run: (id: string) => ['system', 'cleanup', 'run', id] as const,
};

const ACTIVE_RUN = new Set<CleanupRun['status']>(['queued', 'running']);
export const isRunActive = (run: Pick<CleanupRun, 'status'> | null | undefined): boolean => Boolean(run && ACTIVE_RUN.has(run.status));

/** Current machine state. The sampler writes every minute; polling every 30 s stops while the tab is hidden. */
export function useSystemOverview(enabled = true) {
  return useQuery({
    queryKey: systemKeys.overview,
    queryFn: ({ signal }) => api.get<SystemOverview>('/api/system/health/overview', { signal }),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    enabled,
  });
}

export function useSystemSeries(range: SeriesRange, enabled = true) {
  return useQuery({
    queryKey: systemKeys.series(range),
    queryFn: ({ signal }) => api.get<SystemSeries>('/api/system/health/series', { query: { range }, signal }),
    // Switching range keeps the previous chart on screen (dimmed) instead of flashing a skeleton.
    placeholderData: keepPreviousData,
    refetchInterval: range === '1h' ? 60_000 : 5 * 60_000,
    refetchIntervalInBackground: false,
    enabled,
  });
}

export function useSystemStorage(enabled = true) {
  return useQuery({
    queryKey: systemKeys.storage,
    queryFn: ({ signal }) => api.get<StorageBreakdown>('/api/system/storage', { signal }),
    staleTime: 60_000,
    enabled,
  });
}

export function useSystemAlerts(status: AlertStatusFilter, enabled = true) {
  return useQuery({
    queryKey: systemKeys.alerts(status),
    queryFn: ({ signal }) => api.get<{ items: SystemAlert[] }>('/api/system/alerts', { query: { status, limit: 100 }, signal }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

/**
 * Open critical alerts for the sidebar badge: polls every 60 s, only for `system.manage` users.
 * Returns 0 while loading and on error (the badge simply hides).
 */
export function useCriticalAlertCount(enabled: boolean): number {
  const query = useQuery({
    queryKey: systemKeys.alerts('open'),
    queryFn: ({ signal }) => api.get<{ items: SystemAlert[] }>('/api/system/alerts', { query: { status: 'open', limit: 100 }, signal }),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    retry: false,
    enabled,
  });
  if (!enabled || query.isError || !query.data) return 0;
  return query.data.items.filter((a) => a.status === 'open' && a.severity === 'critical').length;
}

export function useAcknowledgeAlert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<SystemAlert>(`/api/system/alerts/${id}/acknowledge`),
    onSuccess: (alert) => {
      for (const status of ['open', 'resolved', 'all'] as const) {
        qc.setQueryData<{ items: SystemAlert[] }>(systemKeys.alerts(status), (prev) =>
          prev ? { ...prev, items: prev.items.map((a) => (a.id === alert.id ? alert : a)) } : prev,
        );
      }
      void qc.invalidateQueries({ queryKey: systemKeys.alertsAll });
      void qc.invalidateQueries({ queryKey: systemKeys.overview });
    },
  });
}

/** Dry run: counts what would be deleted. Nothing changes on the server. */
export function useCleanupPreview() {
  return useMutation({
    mutationFn: (req: CleanupRequest) => api.post<CleanupPreview>('/api/system/cleanup/preview', req),
  });
}

export interface StartCleanupInput {
  request: CleanupRequest;
  confirmPhrase: string;
  /** One key per confirmed intent; reused if the same confirmation is retried. */
  idempotencyKey: string;
}

export function useStartCleanup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ request, confirmPhrase, idempotencyKey }: StartCleanupInput) =>
      api.post<CleanupRun>('/api/system/cleanup', { ...request, confirmPhrase }, { idempotencyKey }),
    onSuccess: (run) => {
      qc.setQueryData(systemKeys.run(run.id), run);
      void qc.invalidateQueries({ queryKey: systemKeys.runs });
    },
  });
}

export function useCleanupRuns(enabled = true) {
  return useQuery({
    queryKey: systemKeys.runs,
    queryFn: ({ signal }) => api.get<{ items: CleanupRun[] }>('/api/system/cleanup/runs', { query: { limit: 20 }, signal }),
    // Keep the list fresh while something is still deleting.
    refetchInterval: (q) => (q.state.data?.items.some(isRunActive) ? 5_000 : false),
    enabled,
  });
}

/** One run; polls every 2 s while queued/running. */
export function useCleanupRun(id: string | null) {
  return useQuery({
    queryKey: systemKeys.run(id ?? ''),
    queryFn: ({ signal }) => api.get<CleanupRun>(`/api/system/cleanup/runs/${id}`, { signal }),
    enabled: Boolean(id),
    refetchInterval: (q) => (q.state.status !== 'error' && (isRunActive(q.state.data) || !q.state.data) ? 2_000 : false),
    refetchIntervalInBackground: true,
  });
}
